import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  Card,
  CardBody,
  Heading,
  useColorModeValue,
  Spinner,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { AddIcon, EditIcon, DeleteIcon, DownloadIcon, AttachmentIcon, ChevronDownIcon } from '@chakra-ui/icons'
import { useBexco, BexcoFaq } from '../contexts/BexcoContext'
import { hasPermission, type Role } from '../config/permissions'

interface VerifiedQA {
  id: number
  question: string
  keyword: string
  answer: string
  category: string
  ui_type?: string
  hit_count: number
  created_at: string
  related_count?: number
  related_questions?: string[]
  related_ids?: number[]
}

const FaqManagementPage: React.FC = () => {
  const { faqs, setFaqs, addFaq, updateFaq, deleteFaq, isLoading, setIsLoading } = useBexco()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingFaq, setEditingFaq] = useState<BexcoFaq | null>(null)
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [selectedFaqs, setSelectedFaqs] = useState<number[]>([])
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'category'>('newest')
  const [verifiedQAs, setVerifiedQAs] = useState<VerifiedQA[]>([])
  const [isLoadingVerifiedQAs, setIsLoadingVerifiedQAs] = useState(false)
  const [selectedVerifiedQAId, setSelectedVerifiedQAId] = useState<number | null>(null) // 🆕 선택된 검증QA ID
  const [verifiedQAPage, setVerifiedQAPage] = useState(1)
  const [verifiedQAPerPage, setVerifiedQAPerPage] = useState(10)
  const [verifiedQASearch, setVerifiedQASearch] = useState('')
  const [verifiedQAFormData, setVerifiedQAFormData] = useState({ question: '', answer: '', keyword: '' })
  const [verifiedQARelatedIds, setVerifiedQARelatedIds] = useState<number[]>([])
  const [isSavingVerifiedQA, setIsSavingVerifiedQA] = useState(false)
  const [totalVerifiedQAs, setTotalVerifiedQAs] = useState(0) // 🆕 전체 검증된 QA 수
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // 권한 확인
  const userRole = (localStorage.getItem('userRole') || 'admin') as Role
  const isAdmin = userRole === 'admin'

  const categories = ['방문자', '대관', '디지털트윈']
  const MAX_FAQ_PER_CATEGORY = 5 // 🆕 카테고리별 최대 FAQ 개수

  // 🆕 카테고리별 FAQ 개수 계산
  const faqCountByCategory = categories.reduce((acc, category) => {
    acc[category] = faqs.filter(faq => faq.category === category).length
    return acc
  }, {} as Record<string, number>)

  // 🆕 검증된 QA가 FAQ에 등록되었는지 확인 (question 텍스트로 매칭)
  const getRegisteredFaqCategory = (qaQuestion: string): string | null => {
    const matchedFaq = faqs.find(faq => 
      faq.question.trim().toLowerCase() === qaQuestion.trim().toLowerCase()
    )
    return matchedFaq ? matchedFaq.category : null
  }

  // 검증된 QA 정렬 (등록된 것은 뒤로)
  const sortedVerifiedQAs = [...verifiedQAs].sort((a, b) => {
    const aRegistered = getRegisteredFaqCategory(a.question) !== null
    const bRegistered = getRegisteredFaqCategory(b.question) !== null
    
    if (aRegistered && !bRegistered) return 1
    if (!aRegistered && bRegistered) return -1
    return (b.hit_count || 0) - (a.hit_count || 0)
  })

  // 키워드 검색 필터링
  const filteredVerifiedQAs = verifiedQASearch.trim()
    ? sortedVerifiedQAs.filter(qa => {
        const searchLower = verifiedQASearch.trim().toLowerCase()
        return (qa.keyword || '').toLowerCase().includes(searchLower)
          || (qa.question || '').toLowerCase().includes(searchLower)
          || (qa.related_questions || []).some(rq => rq.toLowerCase().includes(searchLower))
      })
    : sortedVerifiedQAs

  // 페이지네이션 계산
  const filteredTotal = filteredVerifiedQAs.length
  const totalPages = Math.ceil(filteredTotal / verifiedQAPerPage)
  const paginatedVerifiedQAs = filteredVerifiedQAs.slice(
    (verifiedQAPage - 1) * verifiedQAPerPage,
    verifiedQAPage * verifiedQAPerPage
  )

  // 실제 API에서 FAQ 데이터 로드
  const loadFaqs = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/graph/faqs', {
        method: 'GET',
        credentials: 'include',
      })
      
      if (response.ok) {
        const data = await response.json()
        // 데이터가 배열인지 확인
        if (Array.isArray(data)) {
          setFaqs(data)
        } else if (data && Array.isArray(data.data)) {
          setFaqs(data.data)
        } else {
          console.warn('Unexpected FAQ data format:', data)
          setFaqs([])
        }
      } else {
        throw new Error('FAQ 로드 실패')
      }
    } catch (error) {
      console.error('Failed to load FAQs:', error)
      toast({
        title: '오류',
        description: '자주하는 질문 정보를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }, [setFaqs, setIsLoading, toast])

  useEffect(() => {
    loadFaqs()
  }, [loadFaqs])

  // 🆕 검증된 QA 추천 목록 로드 (hit_count 높은 순)
  const loadVerifiedQAs = useCallback(async () => {
    try {
      setIsLoadingVerifiedQAs(true)
      const response = await fetch('/api/validation/verified-qa/top?limit=500', {
        method: 'GET',
        credentials: 'include',
      })
      
      if (response.ok) {
        const result = await response.json()
        const data = result.data || []
        setVerifiedQAs(data)
        setTotalVerifiedQAs(data.length)
      }
    } catch (error) {
      console.error('Failed to load verified QAs:', error)
    } finally {
      setIsLoadingVerifiedQAs(false)
    }
  }, [])

  useEffect(() => {
    loadVerifiedQAs()
  }, [faqs, loadVerifiedQAs]) // FAQ가 변경될 때마다 다시 로드

  const handleOpenModal = (faq?: BexcoFaq) => {
    if (faq) {
      setEditingFaq(faq)
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: faq.category,
      })
    } else {
      setEditingFaq(null)
      setSelectedVerifiedQAId(null) // 🆕 일반 추가 시 초기화
      setFormData({
        question: '',
        answer: '',
        category: '',
      })
    }
    onOpen()
  }

  // 🆕 검증된 QA 클릭 시 FAQ 추가 모달 열기
  const handleVerifiedQAClick = (qa: VerifiedQA) => {
    // 이미 FAQ에 등록되어 있는지 확인
    const registeredCategory = getRegisteredFaqCategory(qa.question)
    if (registeredCategory) {
      toast({
        title: '이미 등록됨',
        description: `이 질문은 이미 "${registeredCategory}" 카테고리에 FAQ로 등록되어 있습니다.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    
    setEditingFaq(null)
    setSelectedVerifiedQAId(qa.id)
    setFormData({
      question: qa.question,
      answer: qa.answer || '',
      category: qa.category || qa.ui_type || '',
    })
    setVerifiedQAFormData({
      question: qa.question,
      answer: qa.answer || '',
      keyword: qa.keyword || '',
    })
    setVerifiedQARelatedIds(qa.related_ids || [qa.id])
    onOpen()
  }

  // 🆕 검증된 QA 삭제 핸들러
  const handleDeleteVerifiedQA = async () => {
    if (!selectedVerifiedQAId) return
    
    if (!window.confirm('이 검증된 QA를 삭제하시겠습니까?\n\n삭제된 데이터는 복구할 수 없습니다.')) {
      return
    }
    
    try {
      const response = await fetch(`/api/validation/verified-qa/${selectedVerifiedQAId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      
      if (response.ok) {
        toast({
          title: '삭제 완료',
          description: '검증된 QA가 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        // 목록 새로고침
        loadVerifiedQAs()
        onClose()
        setSelectedVerifiedQAId(null)
      } else {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || '삭제 실패')
      }
    } catch (error) {
      console.error('검증된 QA 삭제 오류:', error)
      toast({
        title: '삭제 실패',
        description: error instanceof Error ? error.message : '검증된 QA 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 검증된 QA 수정 저장
  const handleSaveVerifiedQA = async () => {
    if (!selectedVerifiedQAId) return
    setIsSavingVerifiedQA(true)
    try {
      const response = await fetch(`/api/validation/verified-qa/${selectedVerifiedQAId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          answer: verifiedQAFormData.answer,
          keyword: verifiedQAFormData.keyword,
          relatedIds: verifiedQARelatedIds,
        }),
      })
      if (response.ok) {
        toast({
          title: '저장 완료',
          description: '검증된 QA가 수정되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        setFormData({
          question: verifiedQAFormData.question,
          answer: verifiedQAFormData.answer,
          category: formData.category,
        })
        loadVerifiedQAs()
      } else {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || '수정 실패')
      }
    } catch (error) {
      toast({
        title: '저장 실패',
        description: error instanceof Error ? error.message : '검증된 QA 수정에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsSavingVerifiedQA(false)
    }
  }

  const handleSubmit = async () => {
    // 유효성 검사
    if (!formData.question.trim()) {
      toast({
        title: '입력 오류',
        description: '질문을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!formData.answer.trim()) {
      toast({
        title: '입력 오류',
        description: '답변을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!formData.category.trim()) {
      toast({
        title: '입력 오류',
        description: '카테고리를 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // 🆕 새로 추가할 때만 카테고리별 5개 제한 체크
    if (!editingFaq) {
      const currentCount = faqCountByCategory[formData.category] || 0
      if (currentCount >= MAX_FAQ_PER_CATEGORY) {
        alert(`"${formData.category}" 카테고리는 이미 ${MAX_FAQ_PER_CATEGORY}개의 FAQ가 등록되어 있습니다.\n다른 카테고리를 선택하거나 기존 FAQ를 삭제해주세요.`)
        return
      }
    }

    try {
      if (editingFaq) {
        // FAQ 수정 API 호출
        const response = await fetch(`/graph/faqs/${editingFaq.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(formData),
        })
        
        if (response.ok) {
          // 서버에서 최신 데이터 다시 로드
          await loadFaqs()
          toast({
            title: '성공',
            description: '자주하는 질문이 수정되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
        } else {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || '자주하는 질문 수정 실패')
        }
      } else {
        // FAQ 추가 API 호출
        const response = await fetch('/graph/faqs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(formData),
        })
        
        if (response.ok) {
          // 서버에서 최신 데이터 다시 로드
          await loadFaqs()
          toast({
            title: '성공',
            description: '새 FAQ가 추가되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
        } else {
          const errorData = await response.json().catch(() => ({}))
          console.error('FAQ 추가 실패:', errorData)
          throw new Error(errorData.message || '자주하는 질문 추가 실패')
        }
      }
    } catch (error) {
      console.error('자주하는 질문 저장 오류:', error)
      toast({
        title: '저장 실패',
        description: error instanceof Error ? error.message : '자주하는 질문 저장에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('정말로 이 FAQ를 삭제하시겠습니까?')) {
      try {
        setIsLoading(true)
        // FAQ 삭제 API 호출
        const response = await fetch(`/graph/faqs/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        })
        
        if (response.ok) {
          // 서버에서 최신 데이터 다시 로드
          await loadFaqs()
          
          // 삭제 성공 토스트 표시
          toast({
            title: '삭제 완료',
            description: 'FAQ가 성공적으로 삭제되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        } else {
          throw new Error('자주하는 질문 삭제 실패')
        }
      } catch (error) {
        console.error('자주하는 질문 삭제 오류:', error)
        toast({
          title: '삭제 실패',
          description: '자주하는 질문 삭제 중 오류가 발생했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  // CSV 내보내기
  const handleExportCSV = () => {
    const headers = ['카테고리', '질문', '답변']
    const rows = filteredFaqs.map(faq => [
      faq.category,
      faq.question,
      faq.answer
    ])
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `faqs_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    
    toast({
      title: '내보내기 완료',
      description: 'CSV 파일이 다운로드되었습니다.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  // CSV 가져오기
  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter(line => line.trim())
        const headers = lines[0].split(',')
        
        const newFaqs = []
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"'))
          if (values && values.length >= 3) {
            newFaqs.push({
              category: values[0],
              question: values[1],
              answer: values[2]
            })
          }
        }

        // 각 FAQ를 API로 추가
        for (const faq of newFaqs) {
          await fetch('/graph/faqs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(faq),
          })
        }

        // 서버에서 최신 데이터 다시 로드
        await loadFaqs()

        toast({
          title: '가져오기 완료',
          description: `${newFaqs.length}개의 FAQ가 추가되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        
        // 파일 입력 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } catch (error) {
        console.error('CSV 가져오기 오류:', error)
        toast({
          title: '오류',
          description: 'CSV 파일 가져오기에 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
    reader.readAsText(file)
  }

  // 대량 삭제
  const handleBulkDelete = async () => {
    if (selectedFaqs.length === 0) {
      toast({
        title: '알림',
        description: '삭제할 FAQ를 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (window.confirm(`선택한 ${selectedFaqs.length}개의 FAQ를 삭제하시겠습니까?`)) {
      try {
        setIsLoading(true)
        const deleteCount = selectedFaqs.length
        
        for (const id of selectedFaqs) {
          await fetch(`/graph/faqs/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          })
        }
        
        // 서버에서 최신 데이터 다시 로드
        await loadFaqs()
        
        setSelectedFaqs([])
        toast({
          title: '삭제 완료',
          description: `${deleteCount}개의 FAQ가 성공적으로 삭제되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } catch (error) {
        console.error('대량 삭제 오류:', error)
        toast({
          title: '삭제 실패',
          description: '자주하는 질문 삭제 중 오류가 발생했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedFaqs.length === filteredFaqs.length) {
      setSelectedFaqs([])
    } else {
      setSelectedFaqs(filteredFaqs.map(faq => faq.id))
    }
  }

  // 개별 선택
  const handleSelectFaq = (id: number) => {
    if (selectedFaqs.includes(id)) {
      setSelectedFaqs(selectedFaqs.filter(faqId => faqId !== id))
    } else {
      setSelectedFaqs([...selectedFaqs, id])
    }
  }

  // FAQ 필터링, 검색 및 정렬
  const filteredFaqs = faqs
    .filter((faq) => {
      const matchesSearch = 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = !filterCategory || faq.category === filterCategory
      return matchesSearch && matchesCategory
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      } else if (sortBy === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      } else if (sortBy === 'category') {
        return a.category.localeCompare(b.category)
      }
      return 0
    })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="bexco.500" />
          <Text color="gray.500">자주하는 질문 정보를 불러오는 중...</Text>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <Box>
        <Heading size="lg" color="bexco.700" mb={2}>
          자주하는 질문 관리
        </Heading>
        <Text color="gray.600">
          자주하는 질문과 답변을 관리하여 사용자에게 더 나은 서비스를 제공하세요.
        </Text>
      </Box>

      {/* 등록된 자주하는 질문 섹션 제목 */}
      <Box>
        <Heading size="md" mb={2}>등록된 자주하는 질문</Heading>
        <Text fontSize="sm" color="gray.600">
          현재 {faqs.length}개의 자주하는 질문이 등록되어 있습니다.
        </Text>
      </Box>

      {/* FAQ 목록 테이블 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Table variant="simple">
            <Thead>
              <Tr>
                {isAdmin && (
                  <Th width="50px">
                    <Checkbox
                      isChecked={selectedFaqs.length === filteredFaqs.length && filteredFaqs.length > 0}
                      isIndeterminate={selectedFaqs.length > 0 && selectedFaqs.length < filteredFaqs.length}
                      onChange={handleSelectAll}
                    />
                  </Th>
                )}
                <Th>카테고리</Th>
                <Th>질문</Th>
                <Th>답변</Th>
                <Th>수정일</Th>
                <Th>액션</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredFaqs.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <Text color="gray.500">
                      {searchTerm || filterCategory ? '검색 결과가 없습니다.' : 'FAQ가 없습니다. 새로운 FAQ를 추가해보세요.'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredFaqs.map((faq) => (
                <Tr key={faq.id}>
                  {isAdmin && (
                    <Td>
                      <Checkbox
                        isChecked={selectedFaqs.includes(faq.id)}
                        onChange={() => handleSelectFaq(faq.id)}
                      />
                    </Td>
                  )}
                  <Td>
                    <Badge colorScheme="blue">{faq.category}</Badge>
                  </Td>
                  <Td maxW="300px" isTruncated>{faq.question}</Td>
                  <Td maxW="400px" isTruncated>{faq.answer}</Td>
                  <Td>{faq.updatedAt ? new Date(faq.updatedAt).toLocaleDateString() : new Date(faq.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="수정"
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => handleOpenModal(faq)}
                      />
                      <IconButton
                        aria-label="삭제"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(faq.id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              )))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* 🆕 검증된 QA 추천 섹션 */}
      <Box mt={8}>
        <Heading size="md" mb={2}>📊 추천: 검증된 자주 묻는 질문</Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          관리자가 검증한 QA 중 조회수가 높은 질문입니다. 클릭하여 FAQ로 등록하세요.
        </Text>

        {isLoadingVerifiedQAs ? (
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text color="gray.500">검증된 QA를 불러오는 중...</Text>
              </VStack>
            </CardBody>
          </Card>
        ) : verifiedQAs.length === 0 ? (
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Text color="gray.500" textAlign="center" py={8}>
                검증된 QA가 없습니다. 쿼리 로그에서 정상 확정하여 QA를 등록해주세요.
              </Text>
            </CardBody>
          </Card>
        ) : (
          <VStack spacing={3} align="stretch">
            {paginatedVerifiedQAs.map((qa) => {
              const registeredCategory = getRegisteredFaqCategory(qa.question)
              const isRegistered = registeredCategory !== null
              
              return (
                <Card
                  key={qa.id}
                  bg={isRegistered ? 'green.50' : cardBg}
                  border="2px"
                  borderColor={isRegistered ? 'green.400' : borderColor}
                  cursor={isRegistered ? 'default' : 'pointer'}
                  opacity={isRegistered ? 0.7 : 1}
                  transition="all 0.2s"
                  _hover={isRegistered ? {} : {
                    borderColor: 'blue.400',
                    shadow: 'md',
                    transform: 'translateY(-2px)',
                  }}
                  onClick={() => !isRegistered && handleVerifiedQAClick(qa)}
                >
                  <CardBody>
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={2} flex={1}>
                        <HStack flexWrap="wrap" spacing={2}>
                          <Text fontWeight="bold" fontSize="md" color="blue.600">
                            🏷️ {qa.keyword || '미분류'}
                          </Text>
                          {isRegistered && (
                            <Badge colorScheme="green" size="sm">✓ FAQ 등록됨 ({registeredCategory})</Badge>
                          )}
                          {(qa.related_count || 0) > 1 && (
                            <Badge colorScheme="orange" size="sm">
                              📎 {qa.related_count}개 통합
                            </Badge>
                          )}
                        </HStack>
                        <HStack spacing={3}>
                          <Text fontSize="sm" color="gray.500">
                            🔥 {qa.hit_count || 0}회 조회
                          </Text>
                        </HStack>
                        {qa.related_questions && qa.related_questions.length > 0 && (
                          <VStack align="start" spacing={1} pl={2} borderLeft="2px" borderColor="gray.200">
                            {qa.related_questions.slice(0, 5).map((rq, idx) => (
                              <Text key={idx} fontSize="sm" color="gray.600" noOfLines={1}>
                                • {rq}
                              </Text>
                            ))}
                            {qa.related_questions.length > 5 && (
                              <Text fontSize="xs" color="gray.400">
                                외 {qa.related_questions.length - 5}개 더...
                              </Text>
                            )}
                          </VStack>
                        )}
                      </VStack>
                      {!isRegistered && (
                        <Button
                          leftIcon={<AddIcon />}
                          colorScheme="blue"
                          size="sm"
                          flexShrink={0}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleVerifiedQAClick(qa)
                          }}
                        >
                          FAQ 등록
                        </Button>
                      )}
                    </HStack>
                  </CardBody>
                </Card>
              )
            })}

            {/* 페이지네이션 + 키워드 검색 */}
            <HStack justify="space-between" align="center" mt={4} py={2}>
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.600">페이지당 표시:</Text>
                <Select 
                  size="sm" 
                  width="80px"
                  value={verifiedQAPerPage}
                  onChange={(e) => {
                    setVerifiedQAPerPage(Number(e.target.value))
                    setVerifiedQAPage(1)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Select>
                <Text fontSize="sm" color="gray.600" ml={2} whiteSpace="nowrap">
                  총 {filteredTotal}개 중 {filteredTotal > 0 ? (verifiedQAPage - 1) * verifiedQAPerPage + 1 : 0}-{Math.min(verifiedQAPage * verifiedQAPerPage, filteredTotal)}개 표시
                </Text>
              </HStack>

              <Input
                size="sm"
                placeholder="키워드 검색..."
                value={verifiedQASearch}
                onChange={(e) => {
                  setVerifiedQASearch(e.target.value)
                  setVerifiedQAPage(1)
                }}
                width="200px"
                borderColor="gray.300"
              />
              
              <HStack spacing={1}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVerifiedQAPage(1)}
                  isDisabled={verifiedQAPage === 1}
                >
                  {'<<'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVerifiedQAPage(p => Math.max(1, p - 1))}
                  isDisabled={verifiedQAPage === 1}
                >
                  {'<'}
                </Button>
                
                {/* 페이지 번호 */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (verifiedQAPage <= 3) {
                    pageNum = i + 1
                  } else if (verifiedQAPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = verifiedQAPage - 2 + i
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={verifiedQAPage === pageNum ? 'solid' : 'outline'}
                      colorScheme={verifiedQAPage === pageNum ? 'blue' : 'gray'}
                      onClick={() => setVerifiedQAPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
                
                {totalPages > 5 && verifiedQAPage < totalPages - 2 && (
                  <>
                    <Text>...</Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setVerifiedQAPage(totalPages)}
                    >
                      {totalPages}
                    </Button>
                  </>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVerifiedQAPage(p => Math.min(totalPages, p + 1))}
                  isDisabled={verifiedQAPage === totalPages}
                >
                  {'>'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVerifiedQAPage(totalPages)}
                  isDisabled={verifiedQAPage === totalPages}
                >
                  {'>>'}
                </Button>
              </HStack>
            </HStack>
          </VStack>
        )}
      </Box>

      {/* FAQ 추가/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>
            {editingFaq ? '자주하는 질문 수정' : selectedVerifiedQAId ? '검증된 QA 관리' : '자주하는 질문 추가'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5}>
              {/* 검증된 QA 수정 영역 */}
              {selectedVerifiedQAId && !editingFaq && (
                <Box w="100%" p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                  <HStack mb={3} justify="space-between">
                    <Text fontWeight="bold" fontSize="sm" color="blue.700">
                      📝 검증된 QA 수정
                    </Text>
                    {verifiedQARelatedIds.length > 1 && (
                      <Badge colorScheme="orange" fontSize="xs">
                        {verifiedQARelatedIds.length}개 QA 일괄 수정
                      </Badge>
                    )}
                  </HStack>
                  <VStack spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">키워드 {verifiedQARelatedIds.length > 1 && '(그룹 내 전체 적용)'}</FormLabel>
                      <Input
                        size="sm"
                        value={verifiedQAFormData.keyword}
                        onChange={(e) => setVerifiedQAFormData({ ...verifiedQAFormData, keyword: e.target.value })}
                        placeholder="쉼표로 구분 (예: 주차장, 주차, 주차요금)"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">답변 {verifiedQARelatedIds.length > 1 && '(그룹 내 전체 적용)'}</FormLabel>
                      <Textarea
                        size="sm"
                        value={verifiedQAFormData.answer}
                        onChange={(e) => setVerifiedQAFormData({ ...verifiedQAFormData, answer: e.target.value })}
                        rows={6}
                      />
                    </FormControl>
                    <HStack w="100%" justify="flex-end">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleSaveVerifiedQA}
                        isLoading={isSavingVerifiedQA}
                      >
                        검증QA 저장
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* FAQ 등록 영역 */}
              {selectedVerifiedQAId && !editingFaq && (
                <Box w="100%">
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>
                    📋 FAQ로 등록하기
                  </Text>
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>카테고리</FormLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>질문</FormLabel>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="자주하는 질문을 입력하세요"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>답변</FormLabel>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="질문에 대한 답변을 입력하세요"
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            {selectedVerifiedQAId && !editingFaq ? (
              <Button 
                colorScheme="red" 
                variant="outline"
                leftIcon={<DeleteIcon />}
                onClick={handleDeleteVerifiedQA}
              >
                삭제
              </Button>
            ) : (
              <Box />
            )}
            <HStack>
              <Button variant="ghost" onClick={onClose}>
                취소
              </Button>
              <Button 
                colorScheme="bexco" 
                onClick={handleSubmit}
                isDisabled={!formData.question.trim() || !formData.answer.trim() || !formData.category.trim()}
              >
                {editingFaq ? '수정' : 'FAQ 등록'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}

export default FaqManagementPage






